import { Box, FontColor, Icon, IconName, toast } from '@palmetto/palmetto-components';
import { useCallback, type CSSProperties } from 'react';

const labelGroupStyle: CSSProperties = { flex: '0 1 auto', minWidth: 0 };
const valueColumnStyle: CSSProperties = { flex: '1 1 0%', minWidth: '80px', overflow: 'hidden' };
const valueLineStyle: CSSProperties = { overflowWrap: 'break-word', width: '100%' };
const singleValueStyle: CSSProperties = { flex: '1 1 0%', minWidth: '80px', overflowWrap: 'break-word' };
const copyButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0 0 0 4px',
  lineHeight: 1,
};

export function PropertyValue({
  className,
  label,
  value,
  icon,
  iconColor,
  intercomTarget,
  copyable,
}: {
  className?: string;
  label: string;
  value: string | string[];
  icon?: IconName;
  iconColor?: FontColor;
  intercomTarget?: string;
  /** Only applicable for `string | string[]` values. If true, a copy button will be displayed next to the value(s) */
  copyable?: boolean;
}) {
  const handleCopy = useCallback(() => {
    try {
      const text = Array.isArray(value) ? value.join('\n') : value;
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success(`${label} Copied`))
        .catch(() => toast.error(`Failed to copy ${label}`));
    } catch (error) {
      toast.error(`Failed to copy ${label}`);
      console.error(`Failed to copy ${label}:`, error);
    }
  }, [value, label]);

  return (
    <Box
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      borderWidth="0 0 xs 0"
      borderColor="separator"
      padding="0 0 xs 0"
      className={className}
      childGap="lg"
    >
      <Box direction="row" gap="2xs" style={labelGroupStyle}>
        <Box color="body-secondary" fontWeight="medium" data-intercom-target={intercomTarget}>
          {label}
        </Box>
        {icon && <Icon name={icon} color={iconColor} />}
      </Box>
      {Array.isArray(value) ? (
        <>
          <Box direction="column" alignItems="flex-end" gap="2xs" style={valueColumnStyle}>
            {value.map((v, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`${v}-${index}`} textAlign="right" style={valueLineStyle}>
                {v}
              </Box>
            ))}
          </Box>
          {copyable && value.length > 0 && (
            <button type="button" style={copyButtonStyle} onClick={handleCopy} aria-label={`Copy ${label}`}>
              <Icon name="copy-document" size="sm" color="white" />
            </button>
          )}
        </>
      ) : (
        <Box direction="row" alignItems="center" justifyContent="flex-end" style={singleValueStyle}>
          <Box textAlign="right">{value}</Box>
          {copyable && value && (
            <button type="button" style={copyButtonStyle} onClick={handleCopy} aria-label={`Copy ${label}`}>
              <Icon name="copy-document" size="sm" color="white" />
            </button>
          )}
        </Box>
      )}
    </Box>
  );
}
